import { paginatedQuery } from 'lib-shared-api-utils';

import { api } from 'app/app.api';

import {
  CreateAutomationApiArg,
  CreateAutomationApiResponse,
  CreateAutomationIntegrationNodeTestApiArg,
  CreateAutomationIntegrationNodeTestApiResponse,
  CreateChatbotExternalTriggerApiArg,
  DelateAutomationApiArg,
  DeleteAutomationApiResponse,
  ReadAutomationApiArg,
  ReadAutomationApiResponse,
  ReadAutomationsApiArg,
  ReadAutomationsApiResponse,
  ReadFlowsSchedulesAutomationsApiArg,
  ReadFlowsSchedulesAutomationsApiResponse,
  RunAutomationApiArg,
  UpdateAutomationApiArg,
  UpdateAutomationApiResponse,
} from './types/automationsApi.types';
import { transformUpdateAutomationArg } from './utils/transformArg';
import {
  transformCreateAutomationIntegrationNodeTestResponse,
  transformReadAutomationResponse,
} from './utils/transformResponse';

export const automationsApi = api
  .enhanceEndpoints({ addTagTypes: ['Automation'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      createAutomation: builder.mutation<
        CreateAutomationApiResponse,
        CreateAutomationApiArg
      >({
        query: (arg) => ({
          version: 1,
          path: 'automations/',
          method: 'POST',
          body: arg,
        }),
      }),
      readAutomation: builder.query<
        ReadAutomationApiResponse,
        ReadAutomationApiArg
      >({
        query: ({ automationId }) => {
          return {
            version: 1,
            path: `automations/${automationId}`,
            method: 'GET',
          };
        },
        transformResponse: transformReadAutomationResponse,
        providesTags: ['Automation'],
      }),
      readAutomations: builder.query<
        ReadAutomationsApiResponse,
        ReadAutomationsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, (params) => {
            return {
              version: 2,
              path: '/automations',
              params,
            };
          }),
      }),
      readFlowsSchedulesAutomations: builder.query<
        ReadFlowsSchedulesAutomationsApiResponse,
        ReadFlowsSchedulesAutomationsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ id }) => ({
            version: 2,
            path: `flows/schedules/${id}/automations`,
          })),
      }),
      updateAutomation: builder.mutation<
        UpdateAutomationApiResponse,
        UpdateAutomationApiArg
      >({
        query: ({ automationId, payload }) => {
          return {
            version: 1,
            path: `automations/${automationId}`,
            method: 'PATCH',
            body: transformUpdateAutomationArg(payload),
          };
        },
        invalidatesTags: ['Automation'],
      }),
      deleteAutomation: builder.mutation<
        DeleteAutomationApiResponse,
        DelateAutomationApiArg
      >({
        query: ({ automationId }) => ({
          version: 1,
          path: `automations/${automationId}`,
          method: 'DELETE',
        }),
      }),
      //reusing automations API and types, but adding chatbotId to the path
      createChatbotExternalTrigger: builder.mutation<
        CreateAutomationApiResponse,
        CreateChatbotExternalTriggerApiArg
      >({
        query: ({ id, ...rest }) => ({
          version: 1,
          path: `automations?chatbotId=${id}`,
          method: 'POST',
          body: {
            ...rest,
          },
        }),
      }),
      createAutomationIntegrationNodeTest: builder.mutation<
        CreateAutomationIntegrationNodeTestApiResponse,
        CreateAutomationIntegrationNodeTestApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'automations/integration_node/test',
          method: 'POST',
          body,
        }),
        transformResponse: transformCreateAutomationIntegrationNodeTestResponse,
      }),
      runAutomation: builder.mutation<void, RunAutomationApiArg>({
        query: ({ automationId, csid }) => ({
          version: 1,
          path: `automations/run/${automationId}`,
          method: 'POST',
          body: { csid },
        }),
      }),
    }),
  });
